// React imports
import React, { ReactNode } from "react";

// Local imports
import SupportEmailAddressLink from "../../components/DesignSystem/Output/SupportEmailAddressLink";

export default function renderPrivacy(): ReactNode {
	return (
		<div className="max-w-3xl mx-auto px-5 py-5 leading-relaxed">
			<h1 className="text-primary font-bold mb-5 text-3xl">
				JobMap Privacy Policy
			</h1>

			<ol className="list-none pl-0 [counter-reset:section]">
				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Introduction</span> JobMap ("we", "our",
					or "us") is committed to protecting your privacy. This Privacy Policy
					explains how we collect, use, disclose, and safeguard your information
					when you use our job searching service.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Information We Collect</span> We collect
					the following types of information:
					<ul className="list-disc ml-10 mt-2">
						<li>Personal information such as your name and email address</li>
						<li>Job searching data</li>
						<li>Personal job history</li>
						<li>
							Information from third-party systems such as Google and Apple
						</li>
					</ul>
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">How We Collect Information</span>
					<ul className="list-disc ml-10 mt-2">
						<li>Directly from you when you provide it to us</li>
						<li>Automatically through the use of our service</li>
						<li>From third-party systems such as Google and Apple</li>
					</ul>
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Purpose of Data Collection</span> We
					collect this information to improve your job searching experience and
					provide our service effectively.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">How We Use Your Information</span> We use
					the collected information solely for the purpose of operating and
					improving our job search product.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Sharing of Your Information</span> We do
					not share your personal information with third parties.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Data Security</span> We implement
					appropriate technical and organizational measures to protect your
					personal information against unauthorized or unlawful processing,
					accidental loss, destruction, or damage.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Your Rights</span> You have the right to
					access, correct, and request deletion of your personal information. To
					exercise these rights, please contact us at{" "}
					<SupportEmailAddressLink />.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Data Retention</span> We retain your
					personal information for 60 days after the end of your use of our
					service.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">International Data Transfers</span> This
					is a US-based product only. We do not transfer your data
					internationally.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Age Restrictions</span> Our service is
					intended for users who are 18 years of age or older.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Children's Privacy</span> Our service does
					not address anyone under the age of 18. We do not knowingly collect
					personally identifiable information from children under 18.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Account Deletion</span> If you delete your
					account, your data will be deleted 60 days after the request.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Automated Decision-Making</span> We do not
					engage in automated decision-making or profiling using your personal
					data.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Changes to This Privacy Policy</span> We
					may update our Privacy Policy from time to time. We will notify you of
					any changes by posting the new Privacy Policy on this page and
					updating the "Last updated" date.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Contact Us</span> If you have any
					questions about this Privacy Policy, please contact us at{" "}
					<SupportEmailAddressLink />.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Cookies and Tracking Technologies</span>{" "}
					We use cookie-based storage in our service. You can choose to disable
					cookies through your individual browser options.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Compliance with Privacy Laws</span> JobMap
					complies with the California Consumer Privacy Act (CCPA). California
					residents have specific rights regarding their personal information.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Data Breach Procedures</span> In the event
					of a data breach, we will promptly identify and secure the breach,
					notify affected users within 72 hours, and take appropriate measures
					to prevent future occurrences.
				</li>

				<li className="mb-5 before:content-[counter(section)_'._'] before:font-bold [counter-increment:section]">
					<span className="font-bold">Do Not Track Requests</span> At this time,
					we do not respond to browser "Do Not Track" signals. To opt out of
					certain types of data collection, please email{" "}
					<SupportEmailAddressLink />.
				</li>
			</ol>
		</div>
	);
}
